import { memo, useState } from 'react'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { Country, isValidPhoneNumber } from 'react-phone-number-input'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import { useLanguage } from '@/hooks/useLanguage'
import { getCountryCode } from '@/utils/countryCode'

import PhoneNumberInput, { PhoneNumberInputProps } from './PhoneNumberInput'

type PhoneNumberInputControllerProps<T extends FieldValues> = UseControllerProps<T> & Omit<PhoneNumberInputProps, 'onBlur' | 'onChange' | 'value'>

const MemoizedPhoneNumberInput = memo(PhoneNumberInput)

export default function PhoneNumberInputController<T extends FieldValues>(props: PhoneNumberInputControllerProps<T>) {
  const lang = useLanguage()
  const { control, defaultCountry = getCountryCode(lang), name, ...rest } = props
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      validate: (value: string) => {
        let errorKey: string | undefined
        if (value && !isValidPhoneNumber(value, countryCode)) {
          errorKey = 'formValidation.phone.invalidNumber'
        }
        return errorKey
      },
    },
  })

  const [countryCode, setCountryCode] = useState<Country | undefined>(defaultCountry)

  return (
    <MemoizedPhoneNumberInput
      {...rest}
      defaultCountry={defaultCountry}
      error={!!error}
      helperText={error?.message ? <ErrorMessage error={error.message} /> : undefined}
      name={field.name}
      onBlur={field.onBlur}
      onChange={field.onChange}
      onCountryChange={setCountryCode}
      value={field.value}
    />
  )
}
